import LanguageUtility from './language-utility';
import EventDispatcher from '../event/event-dispatcher';

/**
 * Contains general utility methods.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class GeneralUtility {
  /**
   * Application context.
   *
   * @type {string}
   * @private
   */
  private static _applicationContext: string;

  /**
   * Current page UID.
   *
   * @type {number}
   * @private
   */
  private static _pageUid: number;

  /**
   * Current ISO 639-1 language code.
   *
   * @type {string}
   * @private
   */
  private static _language: string;

  /**
   * Current language ID.
   *
   * @type {number}
   * @private
   */
  private static _languageId: number;

  /**
   * URL to the Lawyer API.
   *
   * @type {string}
   * @private
   */
  private static _lawyerApiUrl: string;

  /**
   * Event called when the device orientation changed.
   *
   * @type {EventDispatcher<string>}
   * @private
   */
  private static _deviceOrientationChangeEvent = new EventDispatcher<string>();


  /**
   * Gets the application context.
   *
   * @returns {string}
   */
  static get applicationContext(): string {
    if (this._applicationContext === undefined) {
      if (Globals.applicationContext === undefined) {
        throw new Error('Variable "Globals.applicationContext" is missing.');
      }
      this._applicationContext = Globals.applicationContext;
    }

    return this._applicationContext;
  }


  /**
   * Gets the current page UID.
   *
   * @returns {number}
   */
  static get pageUid(): number {
    if (this._pageUid === undefined) {
      if (Globals.pageUid === undefined) {
        throw new Error('Variable "Globals.pageUid" is missing.');
      }
      this._pageUid = parseInt(Globals.pageUid);
    }

    return this._pageUid;
  }


  /**
   * Gets the ISO 639-1 language code.
   *
   * @returns {string}
   */
  static get language(): string {
    if (this._language === undefined) {
      if (Globals.language === undefined) {
        throw new Error('Variable "Globals.language" is missing.');
      }
      this._language = Globals.language;
    }

    return this._language;
  }


  /**
   * Gets the language ID.
   *
   * @returns {number}
   */
  static get languageId(): number {
    if (this._languageId === undefined) {
      if (Globals.languageId === undefined) {
        throw new Error('Variable "Globals.languageId" is missing.');
      }
      this._languageId = parseInt(Globals.languageId);
    }

    return this._languageId;
  }


  /**
   * Gets the URL to the Lawyer API.
   *
   * @returns {string}
   */
  static get lawyerApiUrl(): string {
    if (this._lawyerApiUrl === undefined) {
      if (Globals.lawyerApiUrl === undefined) {
        throw new Error('Variable "Globals.lawyerApiUrl" is missing.');
      }
      this._lawyerApiUrl = Globals.lawyerApiUrl;
    }

    return this._lawyerApiUrl;
  }


  /**
   * Gets the event that is called when the device orientation changed.
   *
   * @returns {EventDispatcher<string>}
   */
  static get deviceOrientationChangeEvent(): EventDispatcher<string> {
    return this._deviceOrientationChangeEvent;
  }


  /**
   * Converts an object to query parameters to be used in e.g. a GET request. Currently one one additional object nesting is supported.
   *
   * @param { [key: string]: any } data Object to convert to query parameters
   * @returns {string[]} Query parameters
   */
  static convertObjectToQueryParameters(data: { [key: string]: any }): string[] {
    const query: string[] = [];

    Object.keys(data).forEach(key0 => {
      if (data[key0]) {
        if (typeof data[key0] === 'object') {
          Object.keys(data[key0]).forEach(key1 => {
            if (data[key0][key1]) {
              query.push(key0 + '[' + key1 + ']' + '=' + data[key0][key1]);
            }
          });
        } else {
          query.push(key0 + '=' + data[key0]);
        }
      }
    });

    return query;
  }


  /**
   * Gets the current page URL without any parameters or hashes.
   *
   * @param {boolean} [trailingSlash] omitted => do nothing, true => add trailing slash, false => remove trailing slash
   * @returns {string} Current URL
   */
  static getCurrentUrl(trailingSlash: boolean = null): string {
    let href = window.location.href;
    href = href.split('?')[0];
    href = href.split('#')[0];
    const length = href.length;

    if (trailingSlash === true && href.charAt(length - 1) !== '/') {
      href += '/';
    } else if (trailingSlash === false && href.charAt(length - 1) === '/') {
      href = href.substring(0, length - 1);
    }

    return href;
  }


  /**
   * Checks if the current devices is a touch device. Note that devices with multi-touch capable touchpads like laptops will also be detected as mobile.
   *
   * @returns {boolean} True when the device is touchable, otherwise false
   * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#Mobile_Device_Detection
   */
  static isTouch(): boolean {
    let hasTouchScreen: boolean;
    if ('maxTouchPoints' in navigator) {
      hasTouchScreen = navigator.maxTouchPoints > 0;
    } else if ('msMaxTouchPoints' in navigator) {
      // @ts-ignore
      hasTouchScreen = navigator.msMaxTouchPoints > 0;
    } else {
      const mQ = window.matchMedia && matchMedia('(pointer:coarse)');
      if (mQ && mQ.media === '(pointer:coarse)') {
        hasTouchScreen = !!mQ.matches;
      } else if ('orientation' in window) {
        hasTouchScreen = true; // deprecated, but good fallback
      } else {
        // Only as a last resort, fall back to user agent sniffing
        // @ts-ignore
        const UA = navigator.userAgent;
        hasTouchScreen = (
          /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
          /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
        );
      }
    }

    return hasTouchScreen;
  }


  /**
   * Gets the current device orientation.
   *
   * @returns {string} "portrait" or "landscape"
   */
  static getDeviceOrientation(): string {
    if (window.innerHeight > window.innerWidth) {
      return 'portrait';
    } else {
      return 'landscape';
    }
  }


  /**
   * Gets the current browser dimensions.
   *
   * @returns { [key: string]: number } Width and height
   */
  static getBrowserDimensions(): { [key: string]: number } {
    const width = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const height = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    return {
      'width': width,
      'height': height
    };
  }


  /**
   * Gets the current browser scroll position.
   *
   * @returns { [key: string]: number } Top and left
   */
  static getScrollPosition(): { [key: string]: number } {
    const top = document.documentElement.scrollTop || document.body.scrollTop;
    const left = document.documentElement.scrollLeft || document.body.scrollLeft;
    return {
      'top': top,
      'left': left
    };
  }


  /**
   * Gets the offset of an element within the browser.
   *
   * @param {HTMLElement} element Element to get offset from
   * @returns { [key: string]: number } Top and left
   */
  static getOffset(element: HTMLElement): { [key: string]: number } {
    const boundaries = element.getBoundingClientRect();
    const scrollPosition = GeneralUtility.getScrollPosition();
    const top = boundaries.top + scrollPosition.top;
    const left = boundaries.left + scrollPosition.left;
    return {
      'top': top,
      'left': left
    };
  }


  /**
   * Adds a class to an selector for a certain amount of time, then removes it again.
   *
   * @param {HTMLElement} element Element to add class to
   * @param {string} newClass Class to add
   * @param {number} [period] Live-Time of the class in ms, defaults to 1000ms
   * @returns {void}
   */
  static addTimedClass(element: HTMLElement, newClass: string, period: number = 1000) {
    element.classList.add(newClass);
    setTimeout(() => {
      element.classList.remove(newClass);
    }, period);
  }


  /**
   * Gets a random integer within a specific range.
   *
   * @param {number} min Lower boundary (inclusive)
   * @param {number} max Upper boundary (inclusive)
   * @returns {number} Random integer within the boundaries (inclusive)
   */
  static getRandomInteger(min: number, max: number) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }


  /**
   * Creates a loading spinner.
   *
   * @param { [key: string]: any } configuration Configuration of the spinner
   * @param {string} [configuration.type] "spinner-grow" or "spinner-border" (defaults to "spinner-grow")
   * @param {string} [configuration.color] Any of the text-* CSS classes, e,g. text-primary or text-warning (defaults to "text-primary")
   * @param {string} [configuration.text] Loading text (defaults to "Loading...")
   * @param {number} [configuration.size] Size of the spinner in rem
   * @param { [key: string]: any } [configuration.overlay] If set, the spinner is displayed within an overlay
   * @param {boolean} [configuration.overlay.enabled] Whether the overlay is enabled or not (defaults to false)
   * @param {number} [configuration.overlay.opacity] Set CSS opacity of overlay (defaults to 0.75)
   * @param {boolean} [configuration.overlay.class] Additional container classes
   * @param {boolean} [configuration.overlay.color] CSS background-color value for the overlay
   * @param {boolean} [configuration.overlay.colorClass] Color CSS class for the overlay (defaults to bg-white)
   * @returns {HTMLElement} Spinner or spinner overlay containing the spinner
   */
  static createSpinner(configuration: { [key: string]: any }): HTMLElement {
    // Validate configuration
    if (!configuration.hasOwnProperty('type')) {
      configuration.type = 'spinner-grow';
    }

    if (!configuration.hasOwnProperty('color')) {
      configuration.color = 'text-primary';
    }

    if (!configuration.hasOwnProperty('text')) {
      configuration.text = LanguageUtility.getLabel('loading');
      if (configuration.text === undefined || !configuration.text) {
        configuration.text = 'Loading...';
      }
    }

    if (configuration.hasOwnProperty('size')) {
      const size: number = parseInt(configuration.size);
      if (isNaN(size)) {
        configuration.size = null;
      } else {
        configuration.size = size + 'rem';
      }
    }

    if (!configuration.hasOwnProperty('overlay')) {
      configuration.overlay = {};
      configuration.overlay.enabled = false;
    } else {
      configuration.overlay.enabled = true;

      if (!configuration.overlay.hasOwnProperty('opacity')) {
        configuration.overlay.opacity = 0.75;
      }

      if (!configuration.overlay.hasOwnProperty('class')) {
        configuration.overlay.class = 'd-flex justify-content-center';
      }

      if (!configuration.overlay.hasOwnProperty('color')) {
        configuration.overlay.color = null;
      }

      if (!configuration.overlay.hasOwnProperty('colorClass')) {
        configuration.overlay.colorClass = 'bg-white';
      }
    }

    // Create spinner
    const spinner = document.createElement('div');
    spinner.classList.add('spinner', 'align-self-center', configuration.type, configuration.color);

    if (configuration.size) {
      spinner.style.width = configuration.size;
      spinner.style.height = configuration.size;
    }

    if (configuration.text) {
      const srOnly = document.createElement('span');
      srOnly.classList.add('visually-hidden');
      srOnly.innerHTML = configuration.text;
      spinner.append(srOnly);
    }

    if (configuration.overlay.enabled) {
      const overlay = document.createElement('div');
      overlay.classList.add('spinner-overlay');
      overlay.append(spinner);

      const overlayColor = document.createElement('div');
      overlayColor.classList.add('overlay-color');
      overlay.append(overlayColor);

      if (configuration.overlay.opacity !== null) {
        overlayColor.style.opacity = configuration.overlay.opacity;
      }

      if (configuration.overlay.class) {
        overlay.classList.add(...configuration.overlay.class.split(' '));
      }

      if (configuration.overlay.color) {
        overlayColor.style.backgroundColor = configuration.overlay.color;
      }

      if (configuration.overlay.colorClass) {
        overlayColor.classList.add(configuration.overlay.colorClass);
      }

      return overlay;
    }

    return spinner;
  }


  /**
   * Implements a debounced text-seach on the given text input field.
   *
   * @param {HTMLInputElement} inputField Input field to implement search for
   * @param {function} callback Callback when the search changes
   * @param {number} [debounceTimeout] Debounce-delay in milliseconds (defaults to 500)
   * @param {function} [parse] Function to parse the input field value by (defaults to lowercasing and trimming the string-value)
   * @returns {void}
   */
  static debouncedSearch(inputField: HTMLInputElement, callback: any, debounceTimeout: number = 500, parse: any = null) {
    if (debounceTimeout === undefined) {
      debounceTimeout = 500;
    }

    let debounce: boolean = false;
    let timeout: any = null;

    /**
     * Checks if the given key is allowed.
     *
     * @param {number} keyCode Key-code to check
     */
    const isAllowedKey = (keyCode: number): boolean => {
      const allowedKeyCodes: number[] = [
        8, // Backspace
        13, // Enter
        16, // Shift
        17, // Ctrl
        32, // Space
        46, // Delete
        106, // Multiply numpad
        109, // Minus numpad
        173, // Minus
        110, // Comma numpad
        188, // Comma
        59, // ü
        192, // ö
        222 // ä
      ];

      if (allowedKeyCodes.indexOf(keyCode) !== -1) {
        return true;
      }

      // 0-9
      else if (keyCode >= 48 && keyCode <= 57) {
        return true;
      }

      // 0-9 numpad
      else if (keyCode >= 96 && keyCode <= 105) {
        return true;
      }

      // a-z
      else if (keyCode >= 65 && keyCode <= 90) {
        return true;
      }

      return false;
    };

    inputField.addEventListener('keyup', (e) => {
      if (!isAllowedKey(e.keyCode) || debounce) {
        return;
      }

      if (timeout !== null) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => {
        let value = inputField.value;

        if (parse) {
          value = parse(value);
        } else {
          value = value.toLowerCase().trim();
        }

        callback(value);

        debounce = false;
      }, debounceTimeout);

      debounce = true;
    });
  }
}
